import { useState, useEffect } from "react";
import { Button } from "./../components/ui/button";
import { Input } from "./../components/ui/input";
import { useNavigate, Link } from "react-router-dom";
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
} from "./../components/ui/card";
import { Badge } from "./../components/ui/badge";
import { Avatar, AvatarFallback } from "./../components/ui/avatar";
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from "./../components/ui/select";
import { useAuth } from "../context/AuthContext";
import Loader from "./../components/ui/Loader";
import AnimatedBackground from "./../components/ui/AnimatedBackground";
import { getFullImageUrl } from "../utils/imageHandler";
import {
 MapPin,
 Search,
 Code,
 Palette,
 BarChart3,
 Headphones,
 Shield,
 Globe,
 Loader2,
 ChevronLeft,
 ChevronRight,
 Briefcase,
 DollarSign,
 Building2,
} from "lucide-react";

import api from "./../api/client";
import { applierGetJobs } from "./../api/jobs";
import SEOHead from "./../components/SEO/SEOHead";
import { toast } from "react-hot-toast";

export default function Home() {
 const navigate = useNavigate();
 const [keyword, setKeyword] = useState("");
 const [location, setLocation] = useState("");
 const [category, setCategory] = useState("");
 const [searching, setSearching] = useState(false);
 const [jobs, setJobs] = useState([]);
 const [categories, setCategories] = useState([]);
 const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const JOBS_PER_PAGE = 10;
 const { user } = useAuth();

 const fetchJobs = async (filters = {}) => {
 try {
 setLoading(true);
 const res = await applierGetJobs(filters);
  const jobsData = res.data.jobs
  .filter((job) => {
  const isExpired = job.status === "EXPIRED" || (job.deadline && new Date(job.deadline) < new Date());
  return !isExpired;
  })
  .map((job) => ({
  ...job,
  company: {
  name:
  job.companyName || job.recruiter?.companyName || "Unknown Company",
  },
  }));
  setJobs(jobsData);
 setPagination(res.data.pagination);
 setError("");
 } catch (err) {
 console.error(err);
 setJobs([]);
 setError("Failed to load jobs");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 const fetchInitial = async () => {
 try {
 const [catsRes] = await Promise.all([api.get("/jobs/categories")]);
 setCategories(catsRes.data || getFallbackCategories());
 await fetchJobs({ page: 1, limit: JOBS_PER_PAGE });
 } catch (err) {
 console.error(
 "Error fetching categories:",
 err.response?.data || err.message
 );
 setCategories(getFallbackCategories());
 setError("Failed to load categories. Please try again.");
 }
 };
 fetchInitial();
 }, []);

 const handleSearch = async (page = 1, overrides = {}) => {
 setSearching(true);
 try {
 if (overrides.keyword !== undefined) setKeyword(overrides.keyword);
 if (overrides.location !== undefined) setLocation(overrides.location);
 if (overrides.category !== undefined) setCategory(overrides.category);
 const filters = {
 q: (overrides.keyword ?? keyword) || undefined,
 location: (overrides.location ?? location) || undefined,
 category: (overrides.category ?? category) || undefined,
 page,
 limit: JOBS_PER_PAGE,
 };
 await fetchJobs(filters);
 } catch (err) {
 console.error(err);
 setError("Search failed");
 } finally {
 setSearching(false);
 }
 };

 const goToPage = (page) => {
 if (page < 1 || page > pagination.pages || page === pagination.page) return;
 handleSearch(page);
 };

 const getFallbackCategories = () => [
 { icon: Code, title: "Development", count: 245, color: "blue" },
 { icon: Palette, title: "Design", count: 189, color: "purple" },
 { icon: BarChart3, title: "Marketing", count: 132, color: "green" },
 { icon: Headphones, title: "Customer Service", count: 98, color: "orange" },
 { icon: Shield, title: "Security", count: 67, color: "red" },
 { icon: Globe, title: "Sales", count: 154, color: "blue" },
 ];

  const handleJobClick = (job) => {
    const id = job.id || job.jobId;
    if (id) {
      navigate(`/jobs/${id}`);
    } else {
      console.error("Job ID not found for job:", job);
      toast.error("Could not open job details");
    }
  };

 return (
 <div className="relative min-h-screen bg-transparent">
 <AnimatedBackground />
 {/* SEO Meta Tags */}
 <SEOHead
 title="Find Your Dream Job in Ethiopia | Shebeka - Top Job Portal"
 description="Discover thousands of job opportunities from top companies in Ethiopia. Search by location, category, and salary. Apply directly and land your dream job today. Free for job seekers."
 keywords="jobs in Ethiopia, Ethiopian jobs, careers Ethiopia, employment Ethiopia, job search, hiring, recruitment, Addis Ababa jobs, software developer jobs, engineering jobs"
 url="/"
 structuredData={{
 "@context": "https://schema.org",
 "@type": "WebSite",
 "name": "Shebeka",
 "url": "https://shebeka.com",
 "description": "Ethiopia's leading job portal connecting job seekers with top employers",
 "potentialAction": {
 "@type": "SearchAction",
 "target": "https://shebeka.com/jobs?q={search_term_string}",
 "query-input": "required name=search_term_string"
 }
 }}
 />

 {/* HERO SECTION */}
 <section className="relative py-16 md:py-24 lg:py-32 px-4 sm:px-6 lg:px-8">
 <div className="max-w-7xl mx-auto">
 <div className="text-center mb-12">
 <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 text-foreground">
 Find Your <span className="text-primary">Dream Job</span>
 </h1>
 <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
 Thousands of opportunities from top companies across Ethiopia
 </p>
 </div>

 {/* SEARCH BAR */}
 <Card className="max-w-5xl mx-auto shadow-lg">
 <CardContent className="p-6">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
 {/* Keyword Input */}
 <Input
 placeholder="Job title, keywords..."
 value={keyword}
 onChange={(e) => setKeyword(e.target.value)}
 onKeyPress={(e) => e.key === "Enter" && handleSearch()}
 className="h-12"
 />

 {/* Location Input */}
 <Input
 placeholder="Location"
 value={location}
 onChange={(e) => setLocation(e.target.value)}
 onKeyPress={(e) => e.key === "Enter" && handleSearch()}
 className="h-12"
 />

 {/* Category Select */}
 <Select
 value={category}
 onValueChange={(v) => handleSearch(1, { category: v })}
 >
 <SelectTrigger className="h-12">
 <SelectValue placeholder="Category" />
 </SelectTrigger>
 <SelectContent>
 {categories.slice(0, 10).map((c) => (
 <SelectItem key={c.title} value={c.title}>
 {c.title}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>

 {/* Search Button */}
 <Button
 size="lg"
 onClick={() => handleSearch(1)}
 disabled={searching}
 className="h-12 w-full"
 >
 {searching ? (
 <Loader2 className="w-5 h-5 animate-spin mr-2" />
 ) : (
 <Search className="w-5 h-5 mr-2" />
 )}
 Search Jobs
 </Button>
 </div>
 </CardContent>
 </Card>
 </div>
 </section>

 {/* CATEGORIES SECTION */}
 <section className="py-16 px-4 sm:px-6 lg:px-8 ">
 <div className="max-w-7xl mx-auto">
 <div className="text-center mb-12">
 <h2 className="text-3xl md:text-4xl font-bold mb-4">
 Browse by Category
 </h2>
 <p className="text-lg text-muted-foreground">
 Explore jobs in your favorite industry
 </p>
 </div>

 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
 {categories.slice(0, 6).map((cat) => (
 <Card
 key={cat.title}
 onClick={() => handleSearch(1, { category: cat.title })}
 className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-primary"
 >
 <CardContent className="flex flex-col items-center p-6 text-center">
 <div className="p-3 rounded-full bg-primary/10 mb-3">
 {cat.icon ? (
 <cat.icon className="w-6 h-6 text-primary" />
 ) : (
 <Briefcase className="w-6 h-6 text-primary" />
 )}
 </div>
 <p className="font-semibold text-sm mb-1">{cat.title}</p>
 <p className="text-xs text-muted-foreground">{cat.count} Jobs</p>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>
 </section>

 {/* JOBS SECTION */}
 <section className="py-16 px-4 sm:px-6 lg:px-8">
 <div className="max-w-7xl mx-auto">
 {/* Section Header */}
 <div className="flex justify-between items-center mb-10">
 <h2 className="text-3xl md:text-4xl font-bold">
 {keyword || category || location
 ? "Search Results"
 : "Featured Jobs"}
 </h2>
 {error && <p className="text-destructive text-sm">{error}</p>}
 </div>

 {loading ? (
 <Loader />
 ) : jobs.length === 0 ? (
 <Card className="py-16">
 <CardContent className="text-center">
 <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
 <p className="text-lg text-muted-foreground">
 No jobs found. Try adjusting your search criteria.
 </p>
 </CardContent>
 </Card>
 ) : (
 <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
 {jobs.map((job) => (
 <Card
 key={job.id || job.jobId}
 className="group cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-primary"
 onClick={() => handleJobClick(job)}
 >
  <CardHeader className="pb-3">
    <div className="flex items-center gap-3 mb-4">
      <Avatar className="w-12 h-12 rounded-xl border bg-white shadow-sm overflow-hidden transition-transform group-hover:scale-105">
        {(job.companyLogo || job.recruiter?.logoUrl) && (
          <img 
            src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)} 
            alt={job.companyName}
            className="w-full h-full object-contain p-1"
          />
        )}
        <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-black text-sm uppercase">
          {job.companyName?.[0] || job.recruiter?.companyName?.[0] || "J"}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="text-xs font-bold text-primary uppercase tracking-wider truncate mb-0.5">
          {job.company?.name || job.companyName}
        </p>
        <CardTitle className="text-base line-clamp-1 leading-tight">
          {job.title}
        </CardTitle>
      </div>
    </div>
    <div className="flex justify-end gap-2 mb-2">
      {job.badge && (
        <Badge
          variant={
            job.badge === "Urgent"
              ? "destructive"
              : job.badge === "Remote"
              ? "default"
              : "secondary"
          }
          className="text-[10px] px-2 py-0 h-5"
        >
          {job.badge}
        </Badge>
      )}
    </div>
  </CardHeader>

 <CardContent className="space-y-3">
 {/* Location */}
 <div className="flex items-center text-sm text-muted-foreground">
 <MapPin className="w-4 h-4 mr-2 shrink-0" />
 <span className="truncate">{job.location}</span>
 </div>

 {/* Salary */}
 {(job.salaryMin || job.salaryMax) && (
 <div className="flex items-center text-sm font-semibold text-primary">
 <DollarSign className="w-4 h-4 mr-1" />
 {job.salaryMin && job.salaryMax
 ? `${job.salaryMin.toLocaleString()} – ${job.salaryMax.toLocaleString()} ETB`
 : "Competitive"}
 </div>
 )}

 {/* Job Type */}
 <Badge variant="outline" className="text-xs">
 {job.type?.replace("_", " ") || "Full-time"}
 </Badge>

 {/* Action Button */}
 <Button 
 variant="secondary" 
 size="sm" 
 className="w-full mt-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
 >
 View Details
 </Button>
 </CardContent>
 </Card>
 ))}
 </div>
 )}

 {/* Pagination */}
 {pagination.pages > 1 && (
 <div className="flex justify-center items-center gap-2 mt-12">
 <Button
 variant="outline"
 size="icon"
 onClick={() => goToPage(pagination.page - 1)}
 disabled={pagination.page === 1 || searching}
 >
 <ChevronLeft className="w-5 h-5" />
 </Button>
 {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
 const pageNum =
 pagination.page <= 3 ? i + 1 : pagination.page - 2 + i;
 if (pageNum > pagination.pages) return null;
 return (
 <Button
 key={pageNum}
 variant={
 pagination.page === pageNum ? "default" : "outline"
 }
 size="sm"
 onClick={() => goToPage(pageNum)}
 >
 {pageNum}
 </Button>
 );
 })}
 <Button
 variant="outline"
 size="icon"
 onClick={() => goToPage(pagination.page + 1)}
 disabled={pagination.page === pagination.pages || searching}
 >
 <ChevronRight className="w-5 h-5" />
 </Button>
 </div>
 )}
 </div>
 </section>

 {/* CTA SECTION */}
 {!user && (
 <section className="py-16 px-4 sm:px-6 lg:px-8 ">
 <Card className="max-w-4xl mx-auto">
 <CardContent className="text-center py-12 px-6">
 <h3 className="text-3xl md:text-4xl font-bold mb-4">
 Ready to Take the Next Step?
 </h3>
 <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
 Join thousands of professionals who found their dream job with us.
 </p>
 <Button asChild size="lg" className="px-8">
 <Link to="/register">
 Get Started Now
 <ChevronRight className="ml-2 w-5 h-5" />
 </Link>
 </Button>
 </CardContent>
 </Card>
 </section>
 )}
 </div>
 );
}
